<template>
  <div>
    <header-component />
    <div class="container-fluid mt--6">
      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h3 class="mb-0" style="text-transform:capitalize;">User List</h3>
          <div class="d-flex align-items-center gap-2">
            <label class="mb-0 mr-2 text-sm text-muted">Role:</label>
            <select v-model="selectedRole" @change="onRoleChange"
              class="form-control form-control-sm" style="width:160px">
              <option value="">All Roles</option>
              <option v-for="r in roles" :key="r.roleId" :value="r.label">{{ r.label }}</option>
            </select>
          </div>
        </div>
        <el-table
          :data="tableData"
          class="table-responsive table-flush"
          header-row-class-name="thead-light"
        >
          <el-table-column
            label="Name"
            prop="name"
            min-width="160">
            <table-filter-component
              slot="header"
              slot-scope="scope"
              :visible-filters="visibleFilters"
              :scope="scope"
              v-model="filters"
            />
            <template slot-scope="scope">
              <span
                v-if="scope.row.lastName != ''"
              >{{ scope.row.firstName | capitalize }} {{ scope.row.lastName | capitalize }}
              <span v-if="scope.row.lastNameMarathi != ''">({{ scope.row.firstNameMarathi }} {{ scope.row.lastNameMarathi }})</span></span>
              <span v-else>{{ scope.row.firstName | capitalize }} <span v-if="scope.row.firstNameMarathi != ''">({{ scope.row.firstNameMarathi }})</span></span>
            </template>
          </el-table-column>

          <el-table-column
            label="Role"
            prop="roleId"
            min-width="100">
            <template slot-scope="scope">
              <span v-if="scope.row.role && scope.row.role.label">
                <badge v-if="scope.row.role.label.toLowerCase() === 'admin'" type="danger">{{ scope.row.role.label }}</badge>
                <badge v-else-if="scope.row.role.label.toLowerCase() === 'customer'" type="success">{{ scope.row.role.label }}</badge>
                <badge v-else-if="scope.row.role.label.toLowerCase() === 'sale person'" type="info">{{ scope.row.role.label }}</badge>
                <badge v-else-if="scope.row.role.label.toLowerCase() === 'manager'" type="primary">{{ scope.row.role.label }}</badge>
                <badge v-else type="secondary">{{ scope.row.role.label }}</badge>
              </span>
            </template>
          </el-table-column>

          <el-table-column
            label="Phone"
            prop="phone"
            min-width="125">
            <table-filter-component
              slot="header"
              slot-scope="scope"
              :visible-filters="visibleFilters"
              :scope="scope"
              v-model="filters"
            />
            <template slot-scope="scope">
              <span style="white-space:nowrap;">{{ scope.row.phone }}</span>
            </template>
          </el-table-column>

          <el-table-column
            label="Locality"
            prop="locality"
            min-width="120"
            show-overflow-tooltip>
            <table-filter-component
              slot="header"
              slot-scope="scope"
              :visible-filters="visibleFilters"
              :scope="scope"
              v-model="filters"
            />
            <template slot-scope="scope">{{ scope.row.locality }}</template>
          </el-table-column>

          <el-table-column label="Current Branch" min-width="130">
            <template slot-scope="scope">
              <span v-if="scope.row.role && scope.row.role.label && scope.row.role.label.toLowerCase() !== 'customer'">
                <span
                  v-if="scope.row.currentBranchId"
                  class="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full"
                  :style="branchBadgeStyle(scope.row.currentBranchId)"
                >
                  {{ branchName(scope.row.currentBranchId) }}
                </span>
                <span v-else class="text-muted text-xs">—</span>
              </span>
            </template>
          </el-table-column>

          <el-table-column label="Linked Branch" min-width="120">
            <template slot-scope="scope">
              <span v-if="scope.row.customerTypes === 'branch' && scope.row.branchId">
                <span
                  class="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full"
                  :style="branchBadgeStyle(scope.row.branchId)"
                >
                  {{ branchName(scope.row.branchId) }}
                </span>
              </span>
            </template>
          </el-table-column>

          <el-table-column min-width="100">
            <template slot-scope="scope">
              <el-dropdown
                slot="dropdown"
                @command="command => handleAction(scope.row, command)">
                <el-button type="primary" size="mini">
                  Actions
                  <i class="el-icon-arrow-down el-icon--right" />
                </el-button>
                <el-dropdown-menu slot="dropdown" class="dropdown-menu dropdown-menu-arrow show">
                  <el-dropdown-item command="edit">Edit</el-dropdown-item>
                  <el-dropdown-item
                    v-if="scope.row.role && scope.row.role.label && scope.row.role.label.toLowerCase() !== 'customer'"
                    command="assignBranch">Assign Branch</el-dropdown-item>
                  <el-dropdown-item
                    v-if="scope.row.role && scope.row.role.label && scope.row.role.label.toLowerCase() !== 'customer'"
                    command="setPassword">Set Password</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>

          <template slot="empty">
            <div class="table-loader"/>
          </template>
        </el-table>

        <div class="card-footer">
          <div
            v-if="meta"
            slot="footer"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
          >
            <div class>
              <p class="card-category">
                Showing {{ meta.from + 1 }} to {{ meta.to }} of {{ meta.total }} entries
                <span v-if="selectedRows.length">&nbsp; &nbsp; {{ selectedRows.length }} rows selected</span>
              </p>
            </div>
            <pagination :meta="meta" @paginate="setPagination" />
          </div>
        </div>
      </div>
    </div>

    <!-- Assign Branch Modal -->
    <div v-if="branchModal"
      class="fixed inset-0 d-flex align-items-center justify-content-center"
      style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.45);z-index:9999;">
      <div class="bg-white rounded shadow-lg p-4" style="width:360px;max-width:95vw;">
        <h5 class="mb-3">Assign Branch — {{ branchUser && branchUser.firstName }} {{ branchUser && branchUser.lastName }}</h5>
        <div class="form-group mb-3">
          <label class="form-control-label">Branch</label>
          <select v-model="selectedBranchForUser" class="form-control form-control-sm">
            <option value="">— Unassigned —</option>
            <option v-for="b in allBranches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
        </div>
        <div class="d-flex gap-2">
          <button @click="closeBranchModal" class="btn btn-secondary btn-sm flex-fill">Cancel</button>
          <button @click="saveBranchAssignment" :disabled="savingBranch" class="btn btn-primary btn-sm flex-fill">
            {{ savingBranch ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Set Password Modal -->
    <div v-if="passwordModal"
      class="fixed inset-0 d-flex align-items-center justify-content-center"
      style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.45);z-index:9999;">
      <div class="bg-white rounded shadow-lg p-4" style="width:360px;max-width:95vw;">
        <h5 class="mb-3">Set Password — {{ passwordUser && passwordUser.firstName }} {{ passwordUser && passwordUser.lastName }}</h5>
        <div class="form-group mb-2">
          <label class="form-control-label">New Password</label>
          <input v-model="newPassword" type="password" class="form-control form-control-sm"
            placeholder="Min 6 characters" @keyup.enter="savePassword" />
        </div>
        <div class="form-group mb-3">
          <label class="form-control-label">Confirm Password</label>
          <input v-model="confirmPassword" type="password" class="form-control form-control-sm"
            placeholder="Repeat password" @keyup.enter="savePassword" />
        </div>
        <p v-if="passwordError" class="text-danger text-sm mb-2">{{ passwordError }}</p>
        <div class="d-flex gap-2">
          <button @click="closePasswordModal" class="btn btn-secondary btn-sm flex-fill">Cancel</button>
          <button @click="savePassword" :disabled="savingPassword" class="btn btn-primary btn-sm flex-fill">
            {{ savingPassword ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import {
  Select,
  Option,
  DatePicker,
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Dialog,
  Button,
} from "element-ui";
import { mapGetters, mapActions } from "vuex";
import { apiHelper } from "@/utils/helpers";
import endpoints from "@/utils/constants";
import headerComponent from "../Components/HeaderComponent";
import pagination from "@/components/AmsaPagination";
import tableFilter from "@/components/TableFilterComponent";

export default {
  filters: {
    capitalize(value) {
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    "header-component": headerComponent,
    pagination,
    "table-filter-component": tableFilter,
  },
  data: () => {
    return {
      pageSize: 50,
      selectedRows: [],
      filters: {},
      visibleFilters: [],
      roles: [],
      selectedRole: "",
      allBranches: [],
      // branch assignment modal
      branchModal: false,
      branchUser: null,
      selectedBranchForUser: "",
      savingBranch: false,
      // set password modal
      passwordModal: false,
      passwordUser: null,
      newPassword: "",
      confirmPassword: "",
      passwordError: "",
      savingPassword: false,
    };
  },

  computed: {
    ...mapGetters({
      userList: "user/getUserList",
      user: "user/getUser",
    }),
    tableData() {
      return this.userList.rows;
    },
    meta() {
      return this.userList.meta;
    },
  },

  watch: {
    filters: {
      deep: true,
      handler: function (value) {
        Object.filter = (obj, predicate) =>
          Object.keys(obj)
            .filter((key) => predicate(obj[key]))
            .reduce((res, key) => ((res[key] = obj[key]), res), {});

        const filtered = Object.filter(value, (i) => i.length > 2);
        if (Object.entries(filtered).length > 0) {
          this.paginate(1, filtered);
        } else {
          this.paginate();
        }
      },
    },
  },

  beforeMount() {
    this.fetchRoles();
    this.fetchAllBranches();
    this.paginate();
  },

  methods: {
    ...mapActions({ getUsers: "user/getUsers", fetchUser: "user/fetchUser" }),

    fetchRoles() {
      apiHelper
        .call("get", `${endpoints.BASE_URL}${endpoints.API.roles}`)
        .then((res) => {
          if (res.status === 200) this.roles = res.data;
        })
        .catch(() => {});
    },

    fetchAllBranches() {
      apiHelper
        .call("get", `${endpoints.LARAVEL_BASE_URL}/api/v1/branches`)
        .then((res) => { this.allBranches = res.data.data || res.data; })
        .catch(() => {});
    },

    branchName(branchId) {
      var b = this.allBranches.find(function(x) { return x.id === branchId; });
      return b ? b.name : branchId;
    },
    branchBadgeStyle(branchId) {
      var b = this.allBranches.find(function(x) { return x.id === branchId; });
      var color = b && b.color;
      if (!color) return { background: '#dbeafe', color: '#1e40af' };
      return { background: color, color: '#fff' };
    },

    onRoleChange() {
      this.paginate(1, this.filters);
    },

    handleAction(user, command) {
      if (command === "edit") {
        this.fetchUser(user.userId).then((res) => {
          if (res) this.$router.push("/users/add/" + user.userId);
        });
      } else if (command === "assignBranch") {
        this.branchUser = user;
        this.selectedBranchForUser = user.currentBranchId || "";
        this.branchModal = true;
      } else if (command === "setPassword") {
        this.passwordUser = user;
        this.newPassword = "";
        this.confirmPassword = "";
        this.passwordError = "";
        this.passwordModal = true;
      }
    },

    // Branch assignment
    closeBranchModal() {
      this.branchModal = false;
      this.branchUser = null;
      this.selectedBranchForUser = "";
    },
    saveBranchAssignment() {
      this.savingBranch = true;
      apiHelper
        .call("patch", `${endpoints.LARAVEL_BASE_URL}/api/v1/users/${this.branchUser.userId}/branch`, {
          branch_id: this.selectedBranchForUser || null,
        })
        .then(() => {
          this.branchUser.currentBranchId = this.selectedBranchForUser || null;
          var name = this.branchName(this.selectedBranchForUser);
          window.notify({ message: "Branch assigned: " + (name || "Unassigned"), type: "success", title: "Saved" });
          this.closeBranchModal();
        })
        .catch((err) => {
          var msg = (err && err.response && err.response.data && err.response.data.message) || "Failed to assign branch.";
          window.notify({ message: msg, type: "danger", title: "Error" });
        })
        .finally(() => { this.savingBranch = false; });
    },

    // Set password
    closePasswordModal() {
      this.passwordModal = false;
      this.passwordUser = null;
      this.newPassword = "";
      this.confirmPassword = "";
      this.passwordError = "";
    },
    savePassword() {
      this.passwordError = "";
      if (!this.newPassword || this.newPassword.length < 6) {
        this.passwordError = "Password must be at least 6 characters.";
        return;
      }
      if (this.newPassword !== this.confirmPassword) {
        this.passwordError = "Passwords do not match.";
        return;
      }
      this.savingPassword = true;
      apiHelper
        .call("patch", `${endpoints.BASE_URL}/api/v1/users/${this.passwordUser.userId}/password`, {
          password: this.newPassword,
          password_confirmation: this.confirmPassword,
        })
        .then(() => {
          window.notify({ message: "Password updated successfully.", type: "success", title: "Done" });
          this.closePasswordModal();
        })
        .catch((err) => {
          var msg = (err && err.response && err.response.data && err.response.data.message) || "Failed to update password.";
          this.passwordError = msg;
        })
        .finally(() => { this.savingPassword = false; });
    },

    setPagination(data) {
      this.pageSize = data.perPage;
      this.paginate(data.page, this.filters);
    },

    paginate(page = 1, filters = {}) {
      let query = { page, perPage: this.pageSize, ...filters };
      if (this.selectedRole) query.role = this.selectedRole;
      this.getUsers(query);
    },
  },
};
</script>
